import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { MagnifyingGlass, Lock, LockOpen, Trash, Key, ShieldCheck } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const UsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [pwDialog, setPwDialog] = useState(null); // {user_id, new_password}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setUsers(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const action = async (u, payload) => {
    try {
      await axios.post(`${API_URL}/api/admin/users/${u.id || u._id}/action`, payload, { withCredentials: true });
      toast.success('Usuario actualizado');
      load();
    } catch (e) {
      const m = e?.response?.data?.detail || 'Error';
      toast.error(typeof m === 'string' ? m : JSON.stringify(m));
    }
  };

  const del = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.email}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${u.id || u._id}`, { withCredentials: true });
      toast.success('Usuario eliminado');
      load();
    } catch (e) {
      const m = e?.response?.data?.detail || 'Error al eliminar';
      toast.error(typeof m === 'string' ? m : JSON.stringify(m));
    }
  };

  const resetPassword = async () => {
    if (!pwDialog?.new_password || pwDialog.new_password.length < 6) {
      toast.error('Mínimo 6 caracteres');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${pwDialog.user_id}/reset-password`,
        { new_password: pwDialog.new_password },
        { withCredentials: true }
      );
      toast.success('Contraseña reseteada');
      setPwDialog(null);
    } catch (e) {
      const m = e?.response?.data?.detail || 'Error';
      toast.error(typeof m === 'string' ? m : JSON.stringify(m));
    }
  };

  const filtered = users.filter((u) => {
    if (!q) return true;
    const r = q.toLowerCase();
    return (u.email || '').toLowerCase().includes(r) || (u.name || '').toLowerCase().includes(r);
  });

  return (
    <div className="space-y-4" data-testid="users-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold">Usuarios</h2>
          <p className="text-sm text-muted-foreground">{filtered.length} de {users.length} usuarios</p>
        </div>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por email o nombre" className="pl-8 w-64" data-testid="users-search" />
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Cargando...</div>
      ) : (
        <Card className="border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Nombre</th>
                  <th className="text-left px-3 py-2">Rol</th>
                  <th className="text-left px-3 py-2">Estado</th>
                  <th className="text-right px-3 py-2">Descuento</th>
                  <th className="text-right px-3 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const uid = u.id || u._id;
                  const isActive = u.is_active !== false;
                  return (
                    <tr key={uid || u.email} className="border-t border-border/60" data-testid={`user-row-${u.email}`}>
                      <td className="px-3 py-2 text-xs">{u.email}</td>
                      <td className="px-3 py-2">{u.name || '—'}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={u.is_admin ? 'bg-purple-500/15 text-purple-500 border-purple-500/30' : ''}>
                          {u.role || (u.is_admin ? 'admin' : 'user')}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        {isActive ? (
                          <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30" variant="outline">Activo</Badge>
                        ) : (
                          <Badge className="bg-red-500/15 text-red-500 border-red-500/30" variant="outline">Bloqueado</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">{u.discount_percentage || 0}%</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap space-x-1">
                        {isActive ? (
                          <Button size="sm" variant="outline" onClick={() => action(u, { is_active: false })} data-testid={`user-block-${u.email}`}>
                            <Lock size={12} className="mr-1" /> Bloquear
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => action(u, { is_active: true })} data-testid={`user-unblock-${u.email}`}>
                            <LockOpen size={12} className="mr-1" /> Desbloquear
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setPwDialog({ user_id: uid, new_password: '' })} data-testid={`user-reset-${u.email}`}>
                          <Key size={12} className="mr-1" /> Contraseña
                        </Button>
                        {!u.is_admin && (
                          <Button size="sm" variant="outline" onClick={() => action(u, { role: 'admin' })} data-testid={`user-promote-${u.email}`}>
                            <ShieldCheck size={12} className="mr-1" /> Hacer admin
                          </Button>
                        )}
                        {!u.is_admin && (
                          <Button size="sm" variant="outline" onClick={() => del(u)} data-testid={`user-delete-${u.email}`}>
                            <Trash size={12} className="mr-1" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {pwDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="password-dialog">
          <Card className="p-6 max-w-md w-full">
            <h3 className="font-semibold mb-3">Nueva contraseña</h3>
            <Input
              type="password"
              value={pwDialog.new_password}
              onChange={(e) => setPwDialog({ ...pwDialog, new_password: e.target.value })}
              placeholder="Mínimo 6 caracteres"
              data-testid="password-input"
            />
            <div className="flex gap-2 mt-4 justify-end">
              <Button variant="outline" onClick={() => setPwDialog(null)}>Cancelar</Button>
              <Button onClick={resetPassword} data-testid="password-confirm">Guardar</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
